/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { Input, SvgIcon, Table } from "core/components";
import moment from "moment";
import formatter from "core/formatter";
import { price_histories_admin_inventory_item_path } from "routes";
import { api } from "core/api";

type ItemPriceHistoryProps = {
  inventory_item: Record<string, unknown>;
};

const ItemPriceHistory = ({ inventory_item }: ItemPriceHistoryProps): JSX.Element => {
  const [itemPriceHistories, setItemPriceHistories] = React.useState([]);
  const table = React.useRef(null);
  const [loading, setLoading] = React.useState(false);
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "Supplier ID",
        accessor: "supplier.code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: 2,
        Header: "Supplier",
        accessor: "supplier.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: 3,
        Header: "Last Price",
        accessor: "last_price",
        Cell: (props) => {
          const lastPrice = formatter.format(parseFloat(props.value));

          return (
            <div title={lastPrice} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{lastPrice}</p>
            </div>
          );
        }
      },
      {
        id: 4,
        Header: "New Price",
        accessor: "price",
        Cell: (props) => {
          const price = formatter.format(parseFloat(props.value));

          return (
            <div title={price} data-bs-toggle="tooltip">
              <p className="text-truncate text-right">{price}</p>
            </div>
          );
        }
      },
      {
        id: 5,
        Header: "Updated By",
        accessor: "created_by",
        Cell: (props) => {
          const name = `${props.value?.first_name} ${props.value?.last_name}`;
          return (
            <div title={name} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{name}</p>
            </div>
          );
        }
      },
      {
        id: 6,
        Header: "Date Updated",
        accessor: "created_at",
        Cell: (props) => {
          const createdAt = moment(props.value).format("MM/DD/YYYY hh:mm a");

          return (
            <div title={createdAt} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{createdAt}</p>
            </div>
          );
        }
      }
    ],
    []
  );

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" variant="brand">
          <span className="visually-hidden">Loading Price History...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No price history yet.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Supplier ID" onChange={(e) => table.current.setFilter(1, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Supplier Name" onChange={(e) => table.current.setFilter(2, e.target.value)} />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={itemPriceHistories} ref={table} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return itemPriceHistories.length > 0 ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  const getItemPriceHistories = async () => {
    try {
      setLoading(true);
      const response = await api.get(price_histories_admin_inventory_item_path(inventory_item.id));
      setItemPriceHistories(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getItemPriceHistories();
  }, []);

  return (
    <React.StrictMode>
      <Container fluid={true}>{loading ? loader() : content()}</Container>
    </React.StrictMode>
  );
};

export default ItemPriceHistory;
